import {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import ChoiceChips from "@/components/ChoiceChips";
import EmptyData from "@/components/EmptyData";
import MainActions from "@/components/MainActions";
import VLFSelectNew from "@/components/Input/SelectNew";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import {MatchAPI} from "@/api/match";
import {RosterAPI} from "@/api/roster";
import {getInternalClubId} from "@/utils/constants";
import {getRosterName, visibleRosters} from "@/utils/functions";

import "./index.scss";

const dateTimeFormat = "YYYY-MM-DDTHH:mm:ss";

const PERIODS = [
	{label: "Settimana", value: "week"},
	{label: "Mese", value: "month"},
	{label: "Stagione", value: "season"},
];

const periodRange = (key, season, referenceDate) => {
	switch (key) {
		case "week":
			return {
				fromDate: moment(referenceDate)
					.startOf("isoWeek")
					.format(dateTimeFormat),
				toDate: moment(referenceDate).endOf("isoWeek").format(dateTimeFormat),
			};
		case "month":
			return {
				fromDate: moment(referenceDate).startOf("month").format(dateTimeFormat),
				toDate: moment(referenceDate).endOf("month").format(dateTimeFormat),
			};
		case "season":
		default:
			return {
				fromDate: season?.startDate
					? moment(season.startDate).format(dateTimeFormat)
					: moment().startOf("year").format(dateTimeFormat),
				toDate: season?.endDate
					? moment(season.endDate).format(dateTimeFormat)
					: moment().endOf("year").format(dateTimeFormat),
			};
	}
};

// ── Competition label helper ───────────────────────────────────────────────────

const competitionLabel = (match) => {
	return match.competitionName ?? "—";
};

// ── Match row ─────────────────────────────────────────────────────────────────

function MatchRow({match, onClick}) {
	const isPast = moment(match.startDate).isBefore(moment());
	const hasScore = match.homeClubGoals != null && match.awayClubGoals != null;

	// Round label: prefer linked round name, fall back to old text field
	const round = (() => {
		const cs = match.competitionSeason;
		if (cs?.rounds && match.competitionSeasonRoundId) {
			const r = cs.rounds.find((r) => r.id === match.competitionSeasonRoundId);
			if (r)
				return r.name ? `G${r.roundNumber} ${r.name}` : `G${r.roundNumber}`;
		}
		return match.round ?? null;
	})();

	return (
		<div className="ml-row" onClick={onClick}>
			{/* Date + time */}
			<div className="ml-row__date">
				<span className="ml-row__day">
					{moment(match.startDate).format("ddd D")}
				</span>
				<span className="ml-row__time">
					{moment(match.startDate).format("HH:mm")}
				</span>
			</div>

			{/* Competition + season */}
			<div className="ml-row__competition">
				<span className="ml-row__cat" title={competitionLabel(match)}>
					{competitionLabel(match)}
				</span>
				{match.competitionSeason?.season?.name && (
					<span className="ml-row__season">
						{match.competitionSeason.season.name}
					</span>
				)}
			</div>

			{/* Teams + score */}
			<div className="ml-row__fixture">
				<span className="ml-row__team ml-row__team--home">
					{match.homeClub ?? "—"}
				</span>
				{isPast && hasScore ? (
					<span className="ml-row__score">
						{match.homeClubGoals} – {match.awayClubGoals}
					</span>
				) : (
					<span className="ml-row__vs">vs</span>
				)}
				<span className="ml-row__team ml-row__team--away">
					{match.awayClub ?? "—"}
				</span>
			</div>

			{/* Round */}
			{round && <span className="ml-row__round">{round}</span>}
		</div>
	);
}

// ── Month section ─────────────────────────────────────────────────────────────

function MonthSection({monthKey, matches, defaultOpen, onMatchClick}) {
	const [open, setOpen] = useState(defaultOpen);
	const label = moment(monthKey, "YYYY-MM").format("MMMM YYYY");

	return (
		<div className="ml-month">
			<button className="ml-month__header" onClick={() => setOpen((v) => !v)}>
				<span className="ml-month__label">{label}</span>
				<span className="ml-month__count">{matches.length}</span>
				<span className="ml-month__chevron">
					{open ? (
						<ExpandLessIcon fontSize="small" />
					) : (
						<ExpandMoreIcon fontSize="small" />
					)}
				</span>
			</button>

			{open && (
				<div className="ml-month__body">
					{matches.map((m) => (
						<MatchRow key={m.id} match={m} onClick={() => onMatchClick(m.id)} />
					))}
				</div>
			)}
		</div>
	);
}

// ── Page ──────────────────────────────────────────────────────────────────────

function MatchesPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);
	const season = useSelector((state) => state?.season);

	const [loading, setLoading] = useState(false);
	const [matches, setMatches] = useState([]);
	const [rosters, setRosters] = useState([]);
	const [activePeriod, setActivePeriod] = useState("month");
	const [referenceDate, setReferenceDate] = useState(() => new Date());
	const [rosterFilter, setRosterFilter] = useState({
		value: null,
		label: "Tutte le rose",
	});

	// Load rosters for filter
	useEffect(() => {
		if (!seasonId) return;
		RosterAPI.list({seasonId, prospect: false, clubId: getInternalClubId()})
			.then((res) =>
				setRosters([
					{value: null, label: "Tutte le rose"},
					...visibleRosters(
						(res || []).filter(
							(r) => !r.clubId || r.clubId === getInternalClubId(),
						),
					).map((r) => ({value: r.id, label: getRosterName(r)})),
				]),
			)
			.catch(() => {});
	}, [seasonId]);

	// Load matches whenever period or season changes
	useEffect(() => {
		let active = true;
		setMatches([]);
		if (!seasonId) {
			setLoading(false);
			return;
		}
		const {fromDate, toDate} = periodRange(activePeriod, season, referenceDate);
		setLoading(true);
		MatchAPI.list({
			fromDate,
			toDate,
			matchObserved: false,
			clubId: getInternalClubId(),
			seasonId,
		})
			.then((data) => {
				if (active) setMatches(data || []);
			})
			.catch(() => {
				if (active)
					handleNotification("Errore nel caricamento partite", "error");
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [activePeriod, season, referenceDate]); // eslint-disable-line react-hooks/exhaustive-deps

	// Client-side filters
	const filtered = useMemo(() => {
		let result = matches;
		if (rosterFilter?.value) {
			result = result.filter(
				(m) =>
					m.homeRosterId === rosterFilter.value ||
					m.awayRosterId === rosterFilter.value ||
					(!m.homeRosterId &&
						!m.awayRosterId &&
						(m.rosterName === rosterFilter.label ||
							m.playersCategory === rosterFilter.label)),
			);
		}
		return result;
	}, [matches, rosterFilter]);

	// Group by month key "YYYY-MM", sorted chronologically
	const grouped = useMemo(() => {
		const map = {};
		for (const m of filtered) {
			const key = moment(m.startDate).format("YYYY-MM");
			if (!map[key]) map[key] = [];
			map[key].push(m);
		}
		for (const key of Object.keys(map)) {
			map[key].sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
		}
		return Object.keys(map)
			.sort((a, b) => b.localeCompare(a))
			.map((key) => ({key, matches: map[key]}));
	}, [filtered]);

	const currentMonthKey = moment().format("YYYY-MM");

	return (
		<Page className="ml-page">
			<Loading visible={loading} />
			<Breadcrumbs current="Partite" />

			{/* ── Toolbar ───────────────────────────────────────────────── */}
			<div className="ml-toolbar">
				{/* Period selector */}
				<ChoiceChips
					options={PERIODS}
					value={activePeriod}
					onChange={setActivePeriod}
				/>

				{activePeriod !== "season" && (
					<div className="ml-period-navigation">
						<MainActions
							actions={[
								{
									icon: <ChevronLeftIcon />,
									title: "Periodo precedente",
									variant: "outlined",
									onClick: () =>
										setReferenceDate(
											moment(referenceDate).subtract(1, activePeriod).toDate(),
										),
								},
							]}
						/>
						<span className="ml-period-navigation__label">
							{activePeriod === "month"
								? moment(referenceDate).format("MMMM YYYY")
								: moment(referenceDate).startOf("isoWeek").format("D MMM") +
									" – " +
									moment(referenceDate).endOf("isoWeek").format("D MMM YYYY")}
						</span>
						<MainActions
							actions={[
								{
									icon: <ChevronRightIcon />,
									title: "Periodo successivo",
									variant: "outlined",
									onClick: () =>
										setReferenceDate(
											moment(referenceDate).add(1, activePeriod).toDate(),
										),
								},
								{
									label: "Oggi",
									variant: "outlined",
									onClick: () => setReferenceDate(new Date()),
								},
							]}
						/>
					</div>
				)}

				{/* Filters */}
				<div className="ml-filters">
					<div className="ml-filters__select">
						<VLFSelectNew
							options={rosters}
							sorted={false}
							value={rosterFilter}
							onChange={setRosterFilter}
							label="Rosa"
							isClearable={false}
							noMargin
						/>
					</div>
				</div>
			</div>

			{/* ── Timeline ──────────────────────────────────────────────── */}
			<div className="ml-timeline">
				{grouped.length === 0 && !loading ? (
					<EmptyData>Nessuna partita nel periodo selezionato.</EmptyData>
				) : (
					grouped.map(({key, matches: monthMatches}) => (
						<MonthSection
							key={activePeriod + key}
							monthKey={key}
							matches={monthMatches}
							defaultOpen={activePeriod !== "season" || key >= currentMonthKey}
							onMatchClick={(id) =>
								navigate("/matches/" + id, {state: {returnTo: "/matches"}})
							}
						/>
					))
				)}
			</div>
		</Page>
	);
}

export default MatchesPage;
